import React from "react";
import {
  TruckIcon,
  ShieldCheckIcon,
  SupportIcon,
  MapIcon,
  CashIcon,
  StarIcon,
  ThumbUpIcon,
} from "@heroicons/react/outline";
import { InView } from "react-intersection-observer";

const WhyUs = () => {
  const features = [
    {
      icon: <TruckIcon className="w-10 h-10 text-white" />,
      title: "Reliable Fleet of Vehicles",
      description: "From economy cars to luxury SUVs, our fleet is constantly maintained to ensure you have a smooth and reliable journey.",
      gradient: "from-blue-500 to-cyan-500",
      stats: "500+ Vehicles"
    },
    {
      icon: <ShieldCheckIcon className="w-10 h-10 text-white" />,
      title: "Peace of Mind",
      description: "With comprehensive insurance included in every rental, you can travel worry-free knowing you're fully covered.",
      gradient: "from-green-500 to-emerald-500",
      stats: "100% Insured"
    },
    {
      icon: <SupportIcon className="w-10 h-10 text-white" />,
      title: "24/7 Customer Support",
      description: "Our dedicated support team is available round the clock to assist with any questions or issues you may face during your trip.",
      gradient: "from-purple-500 to-pink-500",
      stats: "24/7 Support"
    },
    {
      icon: <MapIcon className="w-10 h-10 text-white" />,
      title: "Wide Service Area",
      description: "Pick up and drop off your vehicle at convenient locations across the country, offering you unparalleled flexibility.",
      gradient: "from-orange-500 to-red-500",
      stats: "50+ Cities"
    },
    {
      icon: <CashIcon className="w-10 h-10 text-white" />,
      title: "No Hidden Charges",
      description: "With transparent pricing and no surprise fees, we offer a straightforward rental experience that you can count on.",
      gradient: "from-yellow-500 to-amber-500",
      stats: "0 Hidden Fees"
    },
    {
      icon: <StarIcon className="w-10 h-10 text-white" />,
      title: "Top-Rated Service",
      description: "Our customers rave about their experiences. Join the thousands who trust us for their travel needs and enjoy top-notch service.",
      gradient: "from-indigo-500 to-blue-500",
      stats: "4.9/5 Rating"
    },
    {
      icon: <ThumbUpIcon className="w-10 h-10 text-white" />,
      title: "Customer Satisfaction",
      description: "We pride ourselves on providing a seamless and enjoyable experience, from booking to returning the vehicle.",
      gradient: "from-teal-500 to-green-500",
      stats: "98% Satisfied"
    },
  ];

  return (
    <div className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <InView triggerOnce={true} threshold={0.3}>
          {({ inView, ref }) => (
            <div ref={ref} className={`text-center mb-16 ${inView ? 'animate__animated animate__fadeInDown' : ''}`}>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Why Choose <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Nupura Cars?</span>
              </h2>
              <p className="text-xl text-blue-200 max-w-3xl mx-auto leading-relaxed">
                Discover the unparalleled advantages that make us the leading choice for 
                premium vehicle rentals and exceptional customer experiences.
              </p>
            </div>
          )}
        </InView>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <InView key={index} triggerOnce={true} threshold={0.2}>
              {({ inView, ref }) => (
                <div
                  ref={ref}
                  className={`group relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:border-white/40 shadow-2xl hover:shadow-3xl transform transition-all duration-500 ${
                    inView 
                      ? 'animate__animated animate__fadeInUp opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-10'
                  } hover:-translate-y-3`}
                >
                  {/* Gradient Icon Container */}
                  <div className={`relative w-20 h-20 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center shadow-2xl mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300 mx-auto`}>
                    {feature.icon}
                    {/* Pulse Effect */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} rounded-2xl opacity-50 group-hover:opacity-75 animate-ping`}></div>
                  </div>

                  {/* Stats Badge */}
                  <div className="absolute top-4 right-4">
                    <span className="bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                      {feature.stats}
                    </span>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-4 text-center group-hover:text-cyan-100 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-blue-100 text-center leading-relaxed group-hover:text-white transition-colors duration-300">
                    {feature.description}
                  </p>

                  {/* Hover Effect Line */}
                  <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-1 bg-gradient-to-r ${feature.gradient} group-hover:w-3/4 transition-all duration-500 rounded-full`}></div>

                  {/* Shimmer Effect */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute -inset-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:inset-full transition-all duration-1000"></div>
                  </div>
                </div>
              )}
            </InView>
          ))}
        </div>

        {/* Bottom Stats Section */}
        <InView triggerOnce={true} threshold={0.3}>
          {({ inView, ref }) => (
            <div ref={ref} className={`mt-16 ${inView ? 'animate__animated animate__fadeInUp' : ''}`}>
              <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                  <div className="text-white">
                    <div className="text-3xl lg:text-4xl font-bold text-cyan-400 mb-2">10K+</div>
                    <div className="text-blue-200">Happy Customers</div>
                  </div>
                  <div className="text-white">
                    <div className="text-3xl lg:text-4xl font-bold text-green-400 mb-2">500+</div>
                    <div className="text-blue-200">Vehicles</div>
                  </div>
                  <div className="text-white">
                    <div className="text-3xl lg:text-4xl font-bold text-purple-400 mb-2">50+</div>
                    <div className="text-blue-200">Cities Covered</div>
                  </div>
                  <div className="text-white">
                    <div className="text-3xl lg:text-4xl font-bold text-yellow-400 mb-2">98%</div>
                    <div className="text-blue-200">Satisfaction Rate</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </InView>

        {/* CTA Section */}
        <InView triggerOnce={true} threshold={0.3}>
          {({ inView, ref }) => (
            <div ref={ref} className={`mt-12 text-center ${inView ? 'animate__animated animate__fadeInUp' : ''}`}>
              <button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold py-4 px-12 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 text-lg">
                Experience Premium Service Today
              </button>
            </div>
          )}
        </InView>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(20px, -20px) scale(1.1); }
          66% { transform: translate(-15px, 15px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 10s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default WhyUs;